import { useCallback, useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { CourtPublic, CourtTypePublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { SchoolFilter, useSchoolFilterIds } from "@/components/school-filter";
import { MultiSelectDropdown } from "@/components/multi-select-dropdown";
import { useGridPreferences } from "@/lib/use-grid-preferences";
import {
  DataTable, applyClientListParams,
  type DataTableColumn, type DataTableFetchParams,
} from "@/components/data-table";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { PreviewPane, PreviewField, PreviewPaneToggle } from "@/components/preview-pane";

export default function CourtsPage() {
  const { user: authUser } = useAuth();
  const canManage = authUser?.roles.some((r) => r.role_type !== "super_parent" && r.role_type !== "parent" && r.role_type !== "player") ?? false;
  const schoolIds = useSchoolFilterIds();
  const [searchParams, setSearchParams] = useSearchParams();
  useGridPreferences("courts");

  const [selected, setSelected] = useState<CourtPublic | null>(null);
  const [pinned, setPinned] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [tableState, setTableState] = useState<"loading" | "error" | "empty" | "ready">("loading");

  const [courtTypes, setCourtTypes] = useState<CourtTypePublic[]>([]);
  useEffect(() => {
    api.getCourtTypes().then((r) => setCourtTypes(r.court_types)).catch(() => {});
  }, []);

  const activeTypes = useMemo(() => {
    const param = searchParams.get("type");
    return param ? new Set(param.split(",")) : new Set<string>();
  }, [searchParams]);
  const typesCsv = [...activeTypes].join(",");

  function updateParams(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace: true });
  }
  function toggleType(typeId: string) {
    const next = new Set(activeTypes);
    if (next.has(typeId)) next.delete(typeId); else next.add(typeId);
    updateParams({ type: next.size > 0 ? [...next].join(",") : null, page: null });
  }

  const effectiveSchoolId = schoolIds[0] ?? "";
  const schoolIdsKey = schoolIds.join(",");
  const isUnscoped = schoolIds.length === 0;

  // unscoped -> GET /courts server-side (type filter via CSV); scoped -> fetch
  // each school's courts then type-filter + search + sort + paginate client-side.
  const fetcher = useCallback(
    async (params: DataTableFetchParams): Promise<{ data: CourtPublic[]; total: number }> => {
      if (isUnscoped) {
        const res = await api.getAllCourts({
          court_type_id: typesCsv || undefined,
          page: params.page, per_page: params.per_page,
          search: params.search, sort: params.sort, order: params.order,
        });
        return { data: res.courts, total: res.total };
      }
      const merged = (
        await Promise.all(schoolIds.map((id) => api.getCourts(id, { search: params.search }).then((r) => r.courts)))
      ).flat();
      const typed = activeTypes.size > 0 ? merged.filter((c) => activeTypes.has(c.court_type_id)) : merged;
      return applyClientListParams(typed, params, {
        sortAccessors: {
          name: (c) => c.name,
          court_type: (c) => c.court_type_name,
          school: (c) => c.school_name,
        },
        defaultSort: "name",
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed via fetchKey
    [schoolIdsKey, typesCsv, isUnscoped],
  );

  const fetchKey = [schoolIdsKey, typesCsv, refreshTick].join("|");

  async function handleDelete(courtId: string) {
    if (!confirm("Are you sure you want to delete this court? All assets will be removed.")) return;
    try {
      await api.deleteCourt(courtId);
      if (selected?.id === courtId) setSelected(null);
      setRefreshTick((t) => t + 1);
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to delete court");
    }
  }

  const columns: DataTableColumn<CourtPublic>[] = [
    { key: "name", header: "Name", sortable: true, className: "font-medium", render: (c) => c.name },
    { key: "court_type", header: "Type", sortable: true, className: "text-muted-foreground", render: (c) => c.court_type_name },
    { key: "school", header: "School", sortable: true, className: "text-muted-foreground", render: (c) => c.school_name },
    { key: "assets", header: "Assets", className: "text-muted-foreground", render: (c) => c.asset_count },
    {
      key: "actions", header: "", headerClassName: "w-32",
      render: (court) => (
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          {court._actions.includes("view") && (
            <Link to={`/courts/${court.id}`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="view">
              <Eye className="h-4 w-4" />
            </Link>
          )}
          {court._actions.includes("edit") && (
            <Link to={`/courts/${court.id}/edit`} className={buttonVariants({ variant: "ghost", size: "sm" })} data-action="edit">
              <Pencil className="h-4 w-4" />
            </Link>
          )}
          {court._actions.includes("delete") && (
            <Button variant="ghost" size="sm" onClick={() => handleDelete(court.id)} data-action="delete">
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full gap-0" data-page="courts-list" data-state={tableState}>
      <div className="min-w-0 flex-1 flex flex-col gap-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Facilities</h1>
            <p className="text-muted-foreground">Manage courts and advertising mounts</p>
          </div>
          <div className="flex gap-2">
            {effectiveSchoolId && canManage && (
              <Link to={`/schools/${effectiveSchoolId}/courts/new`} className={buttonVariants({ size: "sm" })} data-action="create">
                <Plus className="mr-2 h-4 w-4" />
                Add Facility
              </Link>
            )}
            <PreviewPaneToggle pinned={pinned} onTogglePin={() => setPinned((p) => !p)} />
          </div>
        </div>

        {/* Filter bar (school scope + court type). Search/sort/pagination live in the table. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center" data-region="filters">
          <SchoolFilter />
          {courtTypes.length > 0 && (
            <MultiSelectDropdown
              label="Type"
              options={courtTypes.map((ct) => ({ value: ct.id, label: ct.name }))}
              selected={activeTypes}
              onToggle={toggleType}
              onClear={() => updateParams({ type: null, page: null })}
            />
          )}
        </div>

        <div data-region="results">
          <DataTable
            columns={columns}
            fetcher={fetcher}
            fetchKey={fetchKey}
            rowKey={(c) => c.id}
            rowProps={(c) => ({ "data-item": "court", "data-item-id": c.id })}
            rowClassName={(c) => (selected?.id === c.id ? "bg-muted" : undefined)}
            searchPlaceholder="Search facilities..."
            defaultSort="name"
            emptyMessage="No facilities yet."
            onRowClick={(c) => setSelected(c)}
            onStateChange={setTableState}
          />
        </div>
      </div>
      <PreviewPane pinned={pinned} onTogglePin={() => setPinned((p) => !p)} title="Facility">
        {selected && (
          <>
            <PreviewField label="Name">{selected.name}</PreviewField>
            <PreviewField label="Type">{selected.court_type_name}</PreviewField>
            <PreviewField label="School">{selected.school_name}</PreviewField>
            <PreviewField label="Assets">{selected.asset_count}</PreviewField>
          </>
        )}
      </PreviewPane>
    </div>
  );
}
